// common/features/profile/family/utils.ts
//
// Relationship helpers derived from the family vocabulary in ./constants.
// Shared between mobile and web — keep free of platform-specific imports
// (no react-native, no DOM APIs).

import type { PlayerFamilyRelationshipGroup } from '../../../services/player/listPlayerFamily'
import {
  FAMILY_INVITE_TYPE_ID,
  FAMILY_INVITE_TYPE_NAME,
  PARENT_GUARDIAN_TYPE_IDS,
  PARENT_GUARDIAN_TYPE_NAMES,
  RELATIONSHIP_TYPE_BY_ID,
  SELF_RELATIONSHIP_TYPE_ID,
} from './constants'

/** Normalize a relationship name for matching: lowercase, alnum only. */
const normalizeRelationshipName = (name?: string | null): string =>
  (name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')
import type { FamilyInvitationBody, FamilyInvite } from './types'

/**
 * Whether the contact's relationship is "Self" (the player themselves). Such a
 * contact is not a real family member and is excluded from the Family
 * Management list.
 */
export const isSelfRelationship = (
  relationship?: { id?: number | null } | null
): boolean => relationship?.id === SELF_RELATIONSHIP_TYPE_ID

/**
 * Derives the section group from the contact's relationship_type object.
 *
 * Matches by relationship NAME first (Mother/Father/Guardian/Grandparent and
 * grandmother/grandfather variants), because the BE relationship-type ids are
 * not stable across environments — e.g. "Grand Father" comes back as id 7, not
 * the 4 our id map assumes, which previously dropped grandparents into
 * Additional Contacts. The id set is kept only as a fallback. Anything that
 * matches neither (Other, and any missing relationship) goes to
 * additional_contacts so every contact renders somewhere sensible.
 */
export const groupForRelationship = (
  relationship?: { id?: number | null; name?: string | null } | null
): PlayerFamilyRelationshipGroup => {
  const byName = PARENT_GUARDIAN_TYPE_NAMES.has(
    normalizeRelationshipName(relationship?.name)
  )
  const byId =
    relationship?.id != null && PARENT_GUARDIAN_TYPE_IDS.has(relationship.id)
  return byName || byId ? 'parents_guardians' : 'additional_contacts'
}

/**
 * Whether a relationship is worth naming in invitation copy ("…as their X").
 * False when the relationship is missing, "Self" (the player themselves), or
 * resolves to the generic "Other" — in those cases callers should drop the
 * "as their <relation>" clause and just reference the family.
 */
export const hasDisplayableRelationship = (
  relationship?: { id?: number | null; name?: string | null } | null
): boolean =>
  !!relationship &&
  !isSelfRelationship(relationship) &&
  relationshipLabel(relationship) !== 'Other'

/**
 * Display label for a player's family relationship. Prefers the BE-provided
 * `name`, falls back to mapping `id` via RELATIONSHIP_TYPE_BY_ID, else 'Other'.
 */
export const relationshipLabel = (
  relationship?: { id?: number | null; name?: string | null } | null
): string => {
  if (relationship?.name) {
    return relationship.name
  }
  if (relationship?.id != null) {
    return RELATIONSHIP_TYPE_BY_ID[relationship.id] ?? 'Other'
  }
  return 'Other'
}

/**
 * Builds the family-row subtitle: relationship · phone · email, joined with a
 * middle dot. Only present (non-empty) parts are included, so a contact missing
 * a phone shows "Mother · ashleigh@…" rather than a dangling separator.
 */
export const familyMemberDetailLine = (parts: {
  relationship?: string | null
  phone?: string | null
  email?: string | null
}): string =>
  [parts.relationship, parts.phone, parts.email]
    .map((p) => p?.trim())
    .filter((p): p is string => Boolean(p))
    .join(' · ')

/**
 * Reverse of RELATIONSHIP_TYPE_BY_ID — maps a BE relationship label to its
 * numeric id for write payloads. Returns null for an unknown or empty label.
 */
export const relationshipIdFromLabel = (
  label?: string | null
): number | null => {
  if (!label) {
    return null
  }
  const entry = Object.entries(RELATIONSHIP_TYPE_BY_ID).find(
    ([, name]) => name === label
  )
  return entry ? Number(entry[0]) : null
}

/**
 * True when an invite payload is a family (user_to_player) invitation. Matches
 * either the BE type id (13) or the type name, so it survives either changing.
 */
export const isFamilyInvitation = (invite?: FamilyInvite | null): boolean =>
  String(invite?.type?.id) === FAMILY_INVITE_TYPE_ID ||
  invite?.type?.name === FAMILY_INVITE_TYPE_NAME

/**
 * Body-sentence parts for a family invitation. The relation is only surfaced
 * when it is worth naming (present, not Self, not Other) — see
 * hasDisplayableRelationship.
 */
export const familyInvitationBody = (
  invite?: FamilyInvite | null
): FamilyInvitationBody => {
  const playerName =
    invite?.destination?.name_full || invite?.destination?.name || ''
  const hasRelation = hasDisplayableRelationship(invite?.relationship_type)
  const relation = hasRelation
    ? relationshipLabel(invite?.relationship_type).toLowerCase()
    : ''
  return { playerName, relation, hasRelation }
}
